// Campi del profilo del cliente: gli stessi nel profilo del cliente (/profilo) e nella scheda dello staff.
//
// Lingua: gli elenchi qui sotto restano in italiano, perché li legge anche la scheda dello staff; il profilo del
// cliente ne chiede una copia tradotta con scelteIn()/nazionalitaIn(). Nei controlli `lingua` è l'ultimo
// parametro, con l'italiano di base.
import { PRINCIPALE, contesto, traduci } from '../i18n';
import { adessoRoma } from './percorso';

// Etichette corte: nella tendina del profilo "Dipendente a tempo indeterminato" si leggeva "Dipendente a tempo indeterm"
export const CONTRATTI = [['indeterminato', 'Dipendente fisso'], ['determinato', 'Dipendente a termine'],
  ['partita_iva', 'Partita IVA / autonomo'], ['pensionato', 'Pensionato'], ['altro', 'Altro']];
export const STATI_CIVILI = [['celibe_nubile', 'Celibe / nubile'], ['sposato', 'Sposato/a'], ['convivente', 'Convivente'],
  ['divorziato', 'Divorziato/a'], ['vedovo', 'Vedovo/a']];
export const TEMPI_LAVORO = [['meno_1', 'Meno di un anno'], ['1_3', 'Da 1 a 3 anni'], ['oltre_3', 'Più di 3 anni']];
// La ricerca (backend/corrispondenza.py: TIPI_RICERCA e PIANI_RICERCA): più scelte insieme
export const TIPI_IMMOBILE = [['appartamento', 'Appartamento'], ['attico', 'Attico'], ['loft', 'Loft'], ['villa', 'Villa'],
  ['villa_a_schiera', 'Villa a schiera'], ['casa_indipendente', 'Casa indipendente'], ['rustico', 'Rustico o cascina'], ['terreno', 'Terreno']];
export const PIANI_RICERCA = [['no_piano_terra', 'No piano terra'], ['ultimo', 'Ultimo piano'], ['ascensore', 'Con ascensore']];
export const DA_AVERE = [['con_giardino', 'Giardino'], ['con_terrazzo', 'Terrazzo o balcone'], ['con_box', 'Box o posto auto']];
// I comuni della ricerca: erano 5, ora 30 (la zona disegnata sulla mappa ne copre parecchi). Lo stesso limite sta
// nel server (server.MAX_COMUNI_RICERCA, zone.MAX_COMUNI) e in corrispondenza.MAX_COMUNI.
export const MAX_COMUNI = 30;
// La zona disegnata sulla mappa (backend/zone.py): poligoni di punti [lat, lon]. Gli stessi limiti del server.
export const MAX_POLIGONI = 6;
export const MAX_VERTICI = 120;
export const zonaDi = (c) => (Array.isArray(c?.zona_ricerca) ? c.zona_ricerca.filter((p) => Array.isArray(p) && p.length >= 3) : []);
// Due nomi di comune sono lo stesso (come norm del server): "Cassina de' Pecchi" = "cassina de pecchi"
export const nomeComune = (v) => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  .replace(/['’]/g, ' ').replace(/[^a-z0-9]+/g, ' ').trim();
// "piano alto" di prima vale "no piano terra"
export const pianiDi = (c) => (c?.piano_ricerca?.length ? c.piano_ricerca : c?.piano_preferito === 'alto' ? ['no_piano_terra'] : []);
export const tipiDi = (c) => (c?.tipo_immobile_ricerca?.length ? c.tipo_immobile_ricerca : c?.tipo_immobile ? [c.tipo_immobile] : []);
export const RELAZIONI = [['coniuge', 'Coniuge'], ['convivente', 'Convivente'], ['figlio', 'Figlio'], ['figlia', 'Figlia'], ['genitore', 'Genitore'], ['altro', 'Altro']];
// Il permesso di soggiorno di ogni persona del nucleo (backend/permesso_soggiorno.py: TIPI, stesso ordine delle
// schede della pagina «Rinnovo del permesso»). La data scritta qui comanda su quella letta dal documento.
export const TIPI_PERMESSO = [['lavoro_subordinato', 'Lavoro subordinato'], ['lavoro_autonomo', 'Lavoro autonomo'],
  ['famiglia', 'Motivi familiari'], ['lungo_periodo_ue', 'Lungo periodo UE'], ['protezione_temporanea', 'Protezione temporanea'],
  ['studio', 'Studio'], ['altro', 'Altro']];
// Gli anni che il server accetta per la scadenza (permesso_soggiorno.ANNI_AMMESSI)
export const PERMESSO_DAL = '1900-01-01';
export const PERMESSO_AL = '2100-12-31';
export const NAZIONALITA = ['Italia', 'Ucraina', 'Romania', 'Moldavia', 'Albania', 'Marocco', 'Russia', 'Georgia', 'Bielorussia', 'Polonia',
  'Perù', 'Ecuador', 'Egitto', 'Filippine', 'India', 'Cina', 'Senegal', 'Tunisia', 'Brasile'];

// Un elenco di scelte ([[valore, nome]]) nella lingua del cliente: la chiave del dizionario è la sezione più il
// valore stesso ("profilo.scelte.contratti" + "pensionato"). L'italiano resta l'elenco com'è, senza copie inutili.
export const scelteIn = (elenco, sezione, lingua = PRINCIPALE) => (lingua === PRINCIPALE ? elenco
  : elenco.map(([v, n]) => [v, traduci(lingua, `${sezione}.${v}`) || n]));

// La chiave di una nazionalità: il nome senza accenti e in minuscolo ("Perù" → "peru")
const chiaveNazione = (n) => String(n).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
export const nazionalitaIn = (lingua = PRINCIPALE) => (lingua === PRINCIPALE ? NAZIONALITA
  : NAZIONALITA.map((n) => traduci(lingua, `profilo.scelte.nazionalita.${chiaveNazione(n)}`) || n));

// Le tre cose che servono per la lista giusta dei documenti: senza, in home "Completate il profilo in 2 minuti"
export const CAMPI_ESSENZIALI = ['nazionalita', 'tipo_contratto', 'stato_civile'];
export const profiloDaCompletare = (cliente) => Boolean(cliente) && CAMPI_ESSENZIALI.some((k) => !String(cliente[k] || '').trim());

const PAROLE_NUMERO = { uno: 1, una: 1, due: 2, tre: 3, quattro: 4, cinque: 5, sei: 6, sette: 7, otto: 8, nove: 9, dieci: 10 };

// Numeri scritti come si vuole, all'italiana: "250.000", "250 000 €", "1.250.000,50", "92,5", "2.5", "tre".
// Il punto con tre cifre dopo è quello delle migliaia; la virgola (o il punto con una o due cifre) i decimali.
// Vuoto → null; testo che non è un numero → NaN.
export const numeroDa = (v) => {
  let t = String(v ?? '').trim().toLowerCase().replace(/€|euro|m²|mq|m2/g, '').replace(/[\s']/g, '');
  if (!t) return null;
  if (PAROLE_NUMERO[t]) return PAROLE_NUMERO[t];
  const k = /^(\d+(?:[.,]\d+)?)k$/.exec(t); // "250k"
  if (k) return Math.round(Number(k[1].replace(',', '.')) * 1000);
  if (!/^\d[\d.,]*$/.test(t)) return NaN;
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.'); // 1.250.000,50 · 92,5
  else if (/^\d{1,3}(\.\d{3})+$/.test(t)) t = t.replace(/\./g, ''); // 250.000 · 1.250.000
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
};

const INTERI = { locali_min: 'locali', bagni_min: 'bagni', nucleo_familiare: 'persone' };
const EURO = ['budget_min', 'budget_max'];
const OBBLIGATORI = { nome: 'nome_vuoto', cognome: 'cognome_vuoto' };
// "2026-09-15": il massimo dei campi data di nascita. Di base il giorno di Roma (adessoRoma), come il server: con il
// telefono fuori dal fuso (a Tokyo è già domani) prima era il giorno del telefono
export const oggiIso = (adesso = adessoRoma()) => `${adesso.getFullYear()}-${String(adesso.getMonth() + 1).padStart(2, '0')}-${String(adesso.getDate()).padStart(2, '0')}`;
const euro = (n) => `${String(Math.trunc(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.')} €`; // i centesimi no, come al server
// Sotto questa cifra non è il prezzo di una casa (stesso limite della stima della rata): "1.5", "0", "250" si riscrivono
export const BUDGET_MINIMO = 5000;
export const METRI_QUADRI = { min: 10, max: 2000 };

// Il valore da mandare al server per un campo (dopo controllaCampo)
// I comuni scritti con la virgola, senza doppioni (senza guardare le maiuscole: "Seregno, seregno" è uno, come fa il
// server in pulisci_ricerca): prima il doppione restava nel campo ma il server lo toglieva, e con 6 nomi di cui un
// doppione la pagina diceva "Al massimo 5 comuni"
export const comuniDa = (valore) => {
  const visti = new Set();
  return String(valore || '').split(',').map((c) => c.trim()).filter((c) => {
    if (!c || visti.has(c.toLowerCase())) return false;
    visti.add(c.toLowerCase());
    return true;
  });
};

// I comuni entrati da soli dagli immobili inseriti (backend/comuni_imparati.py): il server li dice in
// `citta_ricerca_auto` a ogni lettura del cliente e sono quelli che nelle pagine portano l'etichetta «aggiunto dagli
// immobili inseriti», con la ✕ per toglierli. Un comune scritto a mano non è mai lì dentro: resta senza etichetta.
// `comuni` è l'elenco che si vede adesso (nel modulo dello staff si corregge prima di salvare): un comune già tolto
// dal campo non porta più l'etichetta.
export const comuniAuto = (cliente, comuni) => {
  const elenco = Array.isArray(comuni) ? comuni : (cliente?.citta_ricerca || []);
  const dentro = new Set(elenco.filter(Boolean).map(nomeComune));
  const auto = Array.isArray(cliente?.citta_ricerca_auto) ? cliente.citta_ricerca_auto : [];
  return auto.filter((c) => c && dentro.has(nomeComune(c)));
};

// L'elenco dei comuni senza quello tolto con la ✕ ("Cassina de' Pecchi" e "cassina de pecchi" sono lo stesso).
// Si salva come ogni altra modifica dei comuni: il server si ricorda da sé che è stato rifiutato e non lo rimette.
export const senzaComune = (comuni, comune) => (comuni || []).filter((c) => nomeComune(c) !== nomeComune(comune));

export const valorePerServer = (campo, valore) => {
  if (campo === 'citta_ricerca') return comuniDa(Array.isArray(valore) ? valore.join(',') : valore);
  if (INTERI[campo]) return numeroDa(valore);
  if (EURO.includes(campo)) return numeroDa(valore) === null ? null : String(Math.trunc(numeroDa(valore))); // i centesimi no
  if (campo === 'metratura_min') return numeroDa(valore) === null ? null : String(Math.round(numeroDa(valore)));
  if (typeof valore === 'string') return valore.trim() || null;
  return valore;
};

// Controllo prima di salvare: il messaggio da mostrare vicino al campo, oppure null (si può salvare).
// Il server fa gli stessi controlli (400 con il messaggio): qui si evita di mandare ciò che rifiuterebbe.
export const controllaCampo = (campo, valore, cliente = {}, adesso = adessoRoma(), lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const testo = typeof valore === 'string' ? valore.trim() : valore;
  if (OBBLIGATORI[campo] && !testo) return t(`profilo.errori.${OBBLIGATORI[campo]}`);
  if (campo === 'data_di_nascita' && testo) {
    if (testo > oggiIso(adesso)) return t('profilo.errori.nascita_futura');
    if (testo < '1900-01-01') return t('profilo.errori.nascita_controllate');
  }
  // la scadenza del permesso può essere passata (permesso scaduto), ma dentro gli anni che il server accetta
  if (campo === 'permesso_scadenza' && testo && (testo < PERMESSO_DAL || testo > PERMESSO_AL)) {
    return t('profilo.errori.permesso_scadenza');
  }
  if (INTERI[campo] || EURO.includes(campo) || campo === 'metratura_min') {
    const n = numeroDa(testo);
    if (n === null) return null;
    // metri quadri: un intero plausibile (prima "0,5" diventava 1 m² e "1.000.000" passava)
    if (campo === 'metratura_min') {
      return Number.isNaN(n) || !Number.isInteger(n) || n < METRI_QUADRI.min || n > METRI_QUADRI.max
        ? t('profilo.errori.metri', { min: METRI_QUADRI.min, max: METRI_QUADRI.max }) : null;
    }
    if (EURO.includes(campo)) {
      // "0", "-5", "abc", "1.5" (che nel messaggio del minimo diventava "2 €"): una cifra in euro
      if (Number.isNaN(n) || n < BUDGET_MINIMO) return t('profilo.errori.euro');
      if (n > 50000000) return t('profilo.errori.cifra_alta');
      const altro = numeroDa(cliente[campo === 'budget_min' ? 'budget_max' : 'budget_min']);
      const min = campo === 'budget_min' ? n : altro;
      const max = campo === 'budget_max' ? n : altro;
      if (min && max && !Number.isNaN(min) && !Number.isNaN(max) && Math.trunc(min) > Math.trunc(max)) {
        return t('profilo.errori.budget_invertito', { min: euro(min), max: euro(max) });
      }
      return null;
    }
    if (Number.isNaN(n) || n <= 0) return t('profilo.errori.numero');
    if (!Number.isInteger(n)) {
      return t('profilo.errori.intero', { cosa: t(`profilo.errori.interi.${INTERI[campo]}`), n: Math.floor(n) || 1 });
    }
    if (n > 20) return t('profilo.errori.numero_alto');
  }
  return null;
};
